import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (signatureDataUrl: string) => void;
  clientName: string;
}

const SignatureModal: React.FC<SignatureModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  clientName 
}) => {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const clear = () => {
    sigCanvas.current?.clear();
  };

  const save = () => {
    if (sigCanvas.current) {
      // Check if canvas is empty
      if (sigCanvas.current.isEmpty()) {
        alert('Please provide your signature before saving.');
        return;
      }
      
      try {
        // Get the canvas element directly
        const canvas = sigCanvas.current.getCanvas();
        const signatureDataUrl = canvas.toDataURL('image/png');
        console.log('Signature captured, length:', signatureDataUrl.length);
        onSave(signatureDataUrl);
      } catch (error) {
        console.error('Error getting signature:', error);
        alert('Failed to capture signature. Please try again.');
      }
    }
  };

  const handleClose = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      if (window.confirm('You have an unsigned signature. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
          <h3 className="text-2xl font-bold text-gray-900">Digital Signature</h3>
          <p className="text-sm text-gray-600 mt-2">
            Please sign below for <span className="font-semibold">{clientName}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            You can sign using your mouse or finger on touch devices
          </p>
        </div>
        
        {/* Signature Canvas */}
        <div className="px-8 py-6 bg-gray-100">
          <div className="border-2 border-gray-300 rounded-lg bg-white shadow-inner">
            <SignatureCanvas
              ref={sigCanvas}
              onBegin={() => setIsDrawing(true)}
              onEnd={() => setIsDrawing(false)}
              canvasProps={{
                className: 'w-full cursor-crosshair',
                style: { 
                  width: '100%', 
                  height: '250px',
                  borderRadius: '0.5rem'
                }
              }}
              backgroundColor="white"
              velocityFilterWeight={0.7}
              minWidth={0.5}
              maxWidth={2.5}
              penColor="black"
            />
          </div>
          
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm text-gray-600">
              {isDrawing ? '✍️ Drawing...' : 'Sign above'}
            </div>
            <button
              onClick={clear}
              className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear
            </button>
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-8 py-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-4">
          <button
            onClick={handleClose}
            className="px-6 py-3 border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={save}
            className="px-6 py-3 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors shadow-lg flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Accept & Sign
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;